import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CellRendererProps } from '../../DataTable.types';
import TextAreaEditor from '../../../../TextEditor/TextAreaEditor';
import { deepCompare, isEmpty } from '../../../../../utils/utils';

export const AddCommentCellRenderer: React.FC<CellRendererProps & { updateCustomFieldStatus?: any }> = ({
    value,
    column,
    row,
    updateCustomFieldStatus,
}) => {
    // Support both plain string + slate value
    const initialPlain = value?.text || value || '';
    const initialRich = value?.textEditorValue || [];

    // Only set initial value on mount or when *editor content* from Redux actually changes
    const [editorValue, setEditorValue] = useState(initialRich);

    // Ref to track if we're updating from user input (to prevent state sync from props)
    const isUserInputRef = useRef(false);

    // Ref to store the latest editor value for comparison and debouncing
    const latestEditorValueRef = useRef(initialRich);

    useEffect(() => {
        // Only update state if the change is coming from props (Redux), not from user input
        // Also check if the value actually changed to prevent unnecessary updates
        if (!isUserInputRef.current && !deepCompare(initialRich, latestEditorValueRef.current)) {
            setEditorValue(initialRich);
            latestEditorValueRef.current = initialRich;
        }
        // Only rerun if value.textEditorValue changes, not just row/col
    }, [initialRich]);

    // Ref to store the debounce timeout
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Debounced function to update custom field status
    const debouncedUpdateCustomFieldStatus = useCallback(
        (richVal: any) => {
            // Clear existing timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            // Set new timeout for debounced update
            debounceTimeoutRef.current = setTimeout(() => {
                if (isEmpty(updateCustomFieldStatus)) {
                    return;
                }
                const plain = Array.isArray(richVal)
                    ? richVal.map((n: any) => (n.children && n.children[0] ? n.children[0].text : n.text || '')).join('\n')
                    : '';
                updateCustomFieldStatus(
                    row,
                    {
                        value: {
                            text: plain,
                            textEditorValue: richVal,
                            activityBy: row?.owner?.name || '',
                            activityDate: new Date().toISOString(),
                            userId: row?.owner?.userId || '',
                            owner: row?.owner || {},
                            tags: [],
                            logCallStatus: '',
                            taggedUsers: [],
                        },
                    },
                    column,
                );
                // Reset the flag after debounced update completes
                isUserInputRef.current = false;
            }, 500); // 500ms debounce delay
        },
        [updateCustomFieldStatus, row, column],
    );

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const handleChange = useCallback(
        (richVal: any) => {
            // Mark that this is user input to prevent state sync from props
            isUserInputRef.current = true;

            // DON'T update state here - updating state causes re-renders that trigger
            // synchronization logic in TextAreaEditor, which resets selection and causes focus loss
            // The TextAreaEditor manages its own internal state during user input
            latestEditorValueRef.current = richVal;

            // Debounce the updateCustomFieldStatus call
            debouncedUpdateCustomFieldStatus(richVal);
        },
        [debouncedUpdateCustomFieldStatus],
    );

    // Use a key that includes editorValue (which only updates when syncing from props)
    // This ensures the editor remounts when props change (from Redux),
    // but stays stable during user input (no focus loss)
    const editorValueKey = useMemo(() => JSON.stringify(editorValue), [editorValue]);
    const editorKey = `${row?.id || row?.dealId || 'unknown'}-${column?.field || 'unknown'}-${editorValueKey}`;

    return (
        <div style={{ minHeight: 44, padding: 2, width: '100%', minWidth: 280, maxWidth: 400 }}>
            <TextAreaEditor key={editorKey} editorInitialData={editorValue} onChange={handleChange} placeholder="Add internal comment..." />
        </div>
    );
};
